import { config } from 'dotenv';
import { getProviderById } from '@/providers';
import { setStoredKey, deleteStoredKey } from './key-store.js';

// Load .env on module import
config({ quiet: true });

export function getApiKeyNameForProvider(providerId: string): string | undefined {
  return getProviderById(providerId)?.apiKeyEnvVar;
}

export function getProviderDisplayName(providerId: string): string {
  return getProviderById(providerId)?.displayName ?? providerId;
}

export function checkApiKeyExistsForProvider(providerId: string): boolean {
  const apiKeyName = getApiKeyNameForProvider(providerId);
  if (!apiKeyName) return true;
  return checkApiKeyExists(apiKeyName);
}

export function checkApiKeyExists(apiKeyName: string): boolean {
  // Stored keys are loaded into process.env at startup (loadStoredKeysIntoEnv),
  // so a single env lookup covers shell env, project .env, and the secure store.
  const value = process.env[apiKeyName];
  return !!(value && value.trim() && !value.trim().startsWith('your-'));
}

/**
 * Persist an API key to the secure credential store (~/.max/credentials, 0600)
 * and apply it to the current process immediately so it takes effect in-session.
 */
export function saveApiKeyToEnv(apiKeyName: string, apiKeyValue: string): boolean {
  const value = apiKeyValue.trim();
  if (!value) return false;
  const saved = setStoredKey(apiKeyName, value);
  if (saved) {
    process.env[apiKeyName] = value;
  }
  return saved;
}

export function saveApiKeyForProvider(providerId: string, apiKey: string): boolean {
  const apiKeyName = getApiKeyNameForProvider(providerId);
  if (!apiKeyName) return false;
  return saveApiKeyToEnv(apiKeyName, apiKey);
}

/** Env var holding the Drishti (Indian equity data) API key — required by the core tools. */
export const DRISHTI_API_KEY_NAME = 'DRISHTI_API_KEY';

/**
 * Remove a stored API key from the secure store and the current process.
 * Note: cannot unset a key that comes from the shell environment for the running
 * process's parent; it only clears it for this session and the credential file.
 */
export function removeApiKey(apiKeyName: string): boolean {
  const removed = deleteStoredKey(apiKeyName);
  if (removed) {
    delete process.env[apiKeyName];
  }
  return removed;
}

/** Mask a key for display, showing only the last few characters. */
export function maskApiKey(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length <= 4) return '••••';
  return `••••${trimmed.slice(-4)}`;
}

export type SearchProviderId = 'exa' | 'perplexity' | 'tavily' | 'langsearch';

export const SEARCH_PROVIDERS: Record<SearchProviderId, { displayName: string; apiKeyEnvVar: string }> = {
  exa: { displayName: 'Exa', apiKeyEnvVar: 'EXASEARCH_API_KEY' },
  perplexity: { displayName: 'Perplexity', apiKeyEnvVar: 'PERPLEXITY_API_KEY' },
  tavily: { displayName: 'Tavily', apiKeyEnvVar: 'TAVILY_API_KEY' },
  langsearch: { displayName: 'LangSearch', apiKeyEnvVar: 'LANGSEARCH_API_KEY' },
};

export function getSearchProviderDisplayName(providerId: SearchProviderId): string {
  return SEARCH_PROVIDERS[providerId].displayName;
}

export function getApiKeyNameForSearchProvider(providerId: SearchProviderId): string {
  return SEARCH_PROVIDERS[providerId].apiKeyEnvVar;
}

export function checkApiKeyForSearchProvider(providerId: SearchProviderId): boolean {
  return checkApiKeyExists(SEARCH_PROVIDERS[providerId].apiKeyEnvVar);
}

export function saveApiKeyForSearchProvider(providerId: SearchProviderId, apiKey: string): boolean {
  return saveApiKeyToEnv(SEARCH_PROVIDERS[providerId].apiKeyEnvVar, apiKey);
}
